/**
 * SettingsNav — Layer-1 responsive section switcher for settings-style pages.
 *
 * Purpose: one shared, data-driven nav so every settings page (admin, publisher,
 * generic) renders an identical control instead of each wiring its own pills.
 *
 * Flow / responsive behaviour (breakpoint matches SettingsLayout's grid):
 *   - Desktop (sm+): vertical pill list in the 200px nav column.
 *   - Mobile (<sm): horizontal underline TabNav — the same control list pages
 *     (Titles / Publishers / Legal) use on phones — scrolls on overflow.
 *
 * RTL-safe: logical properties + text-start. Labels arrive pre-translated.
 *
 * @layer-1 — one change propagates to all settings/profile section navs.
 */
import { cn } from "@/shared/lib/utils";
import { TabNav } from "./DataTable";

/** One selectable settings section. `label` is already translated by the caller. */
export interface SettingsNavSection {
  id: string;
  icon: React.ComponentType<{ size: number }>;
  label: string;
}

interface SettingsNavProps {
  /** Sections to render, in display order. */
  sections: SettingsNavSection[];
  /** Currently active section id. */
  active: string;
  /** Fired with the selected section id. */
  onChange: (id: string) => void;
  className?: string;
}

/** Desktop pill — terracotta text + gold-tinted ring when active; 44px touch target. */
function NavPill({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: React.ComponentType<{ size: number }>;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 px-3 py-3 sm:py-2.5 rounded-[10px] text-[13px] font-medium",
        "w-full text-start transition-all duration-150 border-none",
        "min-h-[44px] sm:min-h-0",
        active ? "font-semibold" : "hover:text-[var(--text-primary)]",
      )}
      style={{
        background: active ? "var(--gold-bg, rgba(196,149,106,0.10))" : "none",
        color: active ? "var(--terracotta)" : "var(--text-secondary)",
        cursor: "pointer",
      }}
    >
      <Icon size={15} />
      {label}
    </button>
  );
}

export function SettingsNav({ sections, active, onChange, className }: SettingsNavProps) {
  return (
    <div className={className}>
      {/* Mobile (<sm): underline tab bar, scrolls horizontally on overflow */}
      <div className="sm:hidden -mt-1 mb-1 overflow-x-auto">
        <TabNav
          tabs={sections.map((s) => ({ key: s.id, label: s.label }))}
          active={active}
          onChange={onChange}
          className="min-w-full w-max"
        />
      </div>

      {/* Desktop (sm+): vertical pill list */}
      <div className="hidden sm:flex sm:flex-col gap-1">
        {sections.map((s) => (
          <NavPill
            key={s.id}
            icon={s.icon}
            label={s.label}
            active={active === s.id}
            onClick={() => onChange(s.id)}
          />
        ))}
      </div>
    </div>
  );
}
